import React from 'react';
import { Helmet } from 'react-helmet-async';

const SITE_NAME = 'Crime Newz';
const DEFAULT_DESCRIPTION =
  'Crime Newz — accurate, incisive, reliable. Your trusted source for crime news, investigations, cyber crime, financial fraud, and breaking justice stories.';
const DEFAULT_IMAGE = 'https://crimenewz.com/logo.jpeg';

function getOrigin() {
  if (typeof window !== 'undefined' && window.location && window.location.origin) {
    return window.location.origin;
  }
  return 'https://crimenewz.com';
}

function absoluteUrl(pathOrUrl) {
  if (!pathOrUrl) return getOrigin();
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
  return `${getOrigin()}${pathOrUrl.startsWith('/') ? '' : '/'}${pathOrUrl}`;
}

/**
 * Universal SEO component: Open Graph, Twitter Card, canonical, JSON-LD.
 * Props:
 *  - title         (string, required)
 *  - description   (string)
 *  - image         (string, absolute or path)
 *  - path          (string, current page path; defaults to window.location.pathname)
 *  - type          ("website" | "article")
 *  - publishedTime (ISO string, for article)
 *  - author        (string, for article)
 *  - section       (string, for article — e.g., category name)
 *  - tags          (string[], for article)
 *  - jsonLd        (object | object[], structured data)
 *  - noindex       (boolean, exclude from search engines)
 */
export default function SEO({
  title,
  description = DEFAULT_DESCRIPTION,
  image = DEFAULT_IMAGE,
  path,
  type = 'website',
  publishedTime,
  author,
  section,
  tags,
  jsonLd,
  noindex = false,
}) {
  const url = absoluteUrl(
    path || (typeof window !== 'undefined' ? window.location.pathname + window.location.search : '/')
  );
  const ogImage = absoluteUrl(image);
  const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} | Crime News & Investigations`;
  const jsonLdArray = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];
  const robotsContent = noindex
    ? 'noindex, nofollow'
    : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1';

  return (
    <Helmet prioritizeSeoTags>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />
      <meta name="robots" content={robotsContent} />
      <meta name="googlebot" content={robotsContent} />

      {/* Open Graph */}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content="en_US" />
      {type === 'article' && publishedTime && (
        <meta property="article:published_time" content={publishedTime} />
      )}
      {type === 'article' && author && <meta property="article:author" content={author} />}
      {type === 'article' && section && <meta property="article:section" content={section} />}
      {type === 'article' &&
        Array.isArray(tags) &&
        tags.map((t) => <meta key={t} property="article:tag" content={t} />)}

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {/* JSON-LD structured data */}
      {jsonLdArray.map((data, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(data)}
        </script>
      ))}
    </Helmet>
  );
}

export { absoluteUrl, SITE_NAME, DEFAULT_DESCRIPTION, DEFAULT_IMAGE };
